import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GetHelpModalTypes } from '@/components/Modals/GetHelp/enums/GetHelpModal.enum';
import Panel from '@/components/Panel/Panel';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { ShippingMethods } from '@/enums/shippingMethod';
import { useIsMobileLayout } from '@/hooks';
import { WonItemLocation } from '@/pages/WonItems/components/WonItemsCard/components/WonItemLocation/WonItemLocation';
import { WonItemsCardHeader } from '@/pages/WonItems/components/WonItemsCard/components/WonItemsHeader/WonItemsCardHeader';
import { shouldAllowReview, shouldDelayReview } from '@/pages/WonItems/components/WonItemsCard/helpers/reviews.util';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchCatalogDetailsIfNeeded } from '@/redux/modules/catalog/detail/catalogDetail.actions';
import { reopenPaidInvoice } from '@/redux/modules/checkout/checkout.actions';
import { openGetHelpModal, openLiveShippingDeadlineModal } from '@/redux/modules/modal';
import { fetchPurchasedShippingQuote } from '@/redux/modules/shippingQuote/shippingQuote.actions';
import { getShippingQuote } from '@/redux/modules/shippingQuote/shippingQuote.selectors';
import { getWonCatalogItemsByLocations } from '@/redux/modules/wonItems/wonItems.selectors';
import { WonCatalog } from '@/types/WonCatalog';
import { WonItemInfo } from '@/types/WonItem';
import { safeFromUnixTime } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCheckoutUrl, getCheckoutUrlATG, getReviewUrl } from '@/utils/urls';

import { StatusBadge } from './components/StatusBadge/StatusBadge';
import { WonItemRow } from './components/WonItemRow/WonItemRow';
import { WonItemsLinks } from './components/WonItemsLinks/WonItemsLinks';
import { GetWonCatalogStatusParams, useGetWonCatalogStatus } from './hooks/useGetWonCatalogStatus';

type Props = {
    isBidderSuspended: boolean;
    reviewed: boolean;
    wonCatalog: WonCatalog;
};

export const WonItemsCard = ({ isBidderSuspended, reviewed, wonCatalog }: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();

    const [loading, setLoading] = useState(false);

    const {
        assumePaid,
        atgpay,
        buyersPremium,
        catalogId,
        currency,
        eligibleShipping,
        invoice,
        items,
        liveAuctioneersPayments,
        paymentStatus,
        saleStartTs,
        sellerId,
        sellerName,
        title,
    } = wonCatalog;
    const itemsByLocation = useAppSelector((state) => getWonCatalogItemsByLocations(state, wonCatalog.catalogId));

    const { shippingMethod, shippingStatus, shippingStatusSecondary, shippingTrackingLink } =
        useAppSelector((state) => getShippingQuote(state, catalogId)) || {};

    const {
        houseShippingMessage,
        houseShippingStatus,
        invoiceId,
        invoiceTotal,
        lastPaymentDate,
        paidAmount,
        paidStatus,
        sent,
    } = invoice;
    const { limitToAddDate, quoteStatus, shippingAvailable } = eligibleShipping || {};
    const saleStart = safeFromUnixTime(saleStartTs);

    const isDue = paidStatus === 'due';
    const isPaid = paidStatus === 'paid';
    const isLiveShipping = shippingMethod === ShippingMethods.LiveShipping;

    const isQuotePurchased = quoteStatus === 'purchased' || (isLiveShipping && isPaid);
    const isQuoteUnpurchased = quoteStatus === 'unpurchased' || (!isLiveShipping && isPaid);
    const shippingAddressId = eligibleShipping?.shippingAddress?.addressId;

    const totalPrice: number = isDue ? invoiceTotal : paidAmount;
    const withinAddShippingDeadline = limitToAddDate?.getTime() > Date.now();

    const canAddLiveShipping =
        isPaid &&
        shippingAvailable &&
        Boolean(quoteStatus) &&
        !isQuotePurchased &&
        isQuoteUnpurchased &&
        withinAddShippingDeadline &&
        Boolean(shippingAddressId);

    const canCheckout = !isPaid && sent && liveAuctioneersPayments && !assumePaid && paidAmount === 0;

    useEffect(() => {
        if (invoiceId) {
            dispatch(fetchPurchasedShippingQuote({ catalogId, invoiceId }));
        }
    }, [catalogId, dispatch, invoiceId, isPaid, isQuotePurchased, wonCatalog]);

    const displayLiveShippingDeadlineModal = () => {
        dispatch(openLiveShippingDeadlineModal());
    };

    const reopenInvoice = useCallback(async () => {
        setLoading(true);
        await dispatch(reopenPaidInvoice({ catalogId, invoiceId, shippingAddressId })).then(() =>
            atgpay ? navigate(getCheckoutUrlATG(catalogId)) : navigate(getCheckoutUrl(catalogId))
        );
        setLoading(false);
    }, [atgpay, catalogId, dispatch, shippingAddressId, invoiceId, navigate]);

    const allowedReview = shouldAllowReview(paidStatus, reviewed, saleStart, isBidderSuspended) && (
        <RouteLink
            size="sm"
            to={getReviewUrl(catalogId)}
        >
            {formatMessage(['write_review'])}
        </RouteLink>
    );

    const reviewLink = shouldDelayReview(saleStart, isBidderSuspended) ? null : allowedReview;
    const catalogItems: WonItemInfo[] = items.map((item) => ({
        ...item,
        catalogId,
        currency,
        sellerId,
    }));

    let statusParams: GetWonCatalogStatusParams = {
        catalogId,
        invoiceId,
        lastPaymentDate,
        primaryStatus: paymentStatus,
        secondaryStatus: isQuotePurchased ? shippingStatusSecondary : undefined,
        shippingMethod: isDue ? undefined : shippingMethod,
        wonCatalog,
    };
    if (houseShippingStatus) {
        statusParams = {
            ...statusParams,
            primaryStatus: houseShippingStatus,
            secondaryStatus: houseShippingMessage,
        };
    } else if (canAddLiveShipping || isQuotePurchased) {
        statusParams = {
            ...statusParams,
            addShippingDate: limitToAddDate,
            primaryStatus: shippingStatus,
            secondaryStatus: shippingStatusSecondary,
            shippingAmount: Number(eligibleShipping?.shippingQuote?.total),
            shippingAvailable: shippingAvailable && isQuoteUnpurchased,
            shippingCurrency: eligibleShipping?.shippingQuote ? eligibleShipping.shippingQuote.currency : undefined,
            shippingMethod: isQuotePurchased ? shippingMethod : undefined,
        };
    } else if (Boolean(shippingStatus) && !isDue) {
        statusParams = {
            ...statusParams,
            primaryStatus: shippingStatus,
            secondaryStatus: shippingStatusSecondary,
            shippingMethod,
        };
    } else if (isDue) {
        statusParams = {
            ...statusParams,
            primaryStatus: paymentStatus,
            secondaryStatus: undefined,
            shippingMethod: undefined,
        };
    }
    const { primaryText, secondaryText } = useGetWonCatalogStatus(statusParams);

    const statusBadge = (
        <StatusBadge
            badgeText={invoice?.houseShippingBadge}
            primaryText={primaryText}
            secondaryText={secondaryText}
            shippingMethod={isQuotePurchased ? shippingMethod : undefined}
            trackingLink={shippingTrackingLink}
        />
    );

    let wonItems: JSX.Element;
    const hasMultipleLocations = Object.keys(itemsByLocation).length > 1;
    let hasSingleStatus = true;
    // has multiple locations, the keys of the object are the addresses of house locations
    if (hasMultipleLocations) {
        hasSingleStatus = Object.keys(itemsByLocation).reduce(
            (isSingleStatusSoFar, hash) => isSingleStatusSoFar && !itemsByLocation[hash].shippingStatus,
            true
        );
        wonItems = (
            // separate into sections if from multiple locations
            <div
                className={cn('flex-2 mt-0 flex flex-col', {
                    'mt-lg': hasSingleStatus,
                })}
            >
                {Object.keys(itemsByLocation).map((locationHash, index) => {
                    return (
                        <span key={locationHash}>
                            <WonItemLocation
                                locationInfo={itemsByLocation[locationHash]}
                                wonCatalog={wonCatalog}
                            />
                            {index < Object.keys(itemsByLocation).length - 1 && (
                                <div
                                    className={cn('mx-0 my-lg w-[905px] border-b border-border-secondary', {
                                        'w-full': isMobile,
                                    })}
                                />
                            )}
                        </span>
                    );
                })}
            </div>
        );
    } else {
        // only has a single location, so no need to separate them into sections
        wonItems = (
            <div className="flex-2 mt-lg flex flex-col">
                {catalogItems.map((item) => (
                    <WonItemRow
                        buyersPremium={buyersPremium}
                        item={item}
                        key={item.itemId}
                        won
                    />
                ))}
            </div>
        );
    }

    let wonItemCardButton: JSX.Element = null;
    const wonItemButtonClasses = 'max-w-[190px] w-full';

    if (canCheckout) {
        wonItemCardButton = (
            <RouteButton
                className={wonItemButtonClasses}
                to={atgpay ? getCheckoutUrlATG(catalogId) : getCheckoutUrl(catalogId)}
            >
                {formatMessage(['wonitems', 'checkout'])}
            </RouteButton>
        );
    } else if (canAddLiveShipping) {
        wonItemCardButton = (
            <Button
                aria-label="Add Shipping"
                className={wonItemButtonClasses}
                loading={loading}
                onClick={!withinAddShippingDeadline ? displayLiveShippingDeadlineModal : reopenInvoice}
            >
                {formatMessage(['wonitems', 'addShipping'])}
            </Button>
        );
    }

    const displayGetHelpModal = async () => {
        await dispatch(fetchCatalogDetailsIfNeeded([catalogId]));
        dispatch(
            openGetHelpModal({
                data: {
                    modalData: {
                        catalogId,
                        invoiceId,
                        invoiceStatus: primaryText,
                        isLivePayments: liveAuctioneersPayments,
                        isLiveShipping,
                        isPaid: isPaid || assumePaid,
                        itemCount: items.length,
                        itemId: items[0].itemId,
                        sellerId,
                    },
                    modalVariant: GetHelpModalTypes.WonItemsPage,
                },
            })
        );
    };

    const wonCatalogLinks = (
        <WonItemsLinks
            atgPayActive={Boolean(atgpay)}
            bidderIsSuspended={isBidderSuspended}
            catalogId={catalogId}
            invoiceId={invoiceId}
            isLivePayments={liveAuctioneersPayments}
            paidStatus={paidStatus}
            reviewed={reviewed}
            reviewLink={reviewLink}
            sent={sent}
            showGetHelpModal={displayGetHelpModal}
        />
    );

    return (
        <Panel
            className="mt-lg border border-border-secondary p-0"
            header={
                <WonItemsCardHeader
                    currency={currency}
                    date={saleStartTs}
                    invoiceId={invoiceId}
                    sellerName={sellerName}
                    title={title}
                    totalPrice={totalPrice}
                />
            }
            key={catalogId}
        >
            <div
                className={cn('m-lg flex justify-between', {
                    'mx-md my-lg block': isMobile,
                })}
            >
                <div className="block">
                    {hasSingleStatus && (
                        <>
                            <div
                                className={cn('flex', {
                                    'mt-lg max-w-[95%]': isMobile,
                                })}
                            >
                                {statusBadge}
                            </div>
                            <div className="mt-sm">{wonItemCardButton}</div>
                        </>
                    )}
                    {isMobile && <div className="mx-0 my-lg">{wonCatalogLinks}</div>}
                    {wonItems}
                </div>
                {!isMobile && <div className="ml-lg flex flex-col text-right">{wonCatalogLinks}</div>}
            </div>
        </Panel>
    );
};
